/* =========================================================================
 * CONFERENCE TITLES — a conference crowns one champion, or two, and which it
 * is depends on the SPORT and sometimes on the CONFERENCE.
 *
 * Brian: "Yes, conference regular title and tournament title get recorded."
 *
 * ⚠️ WHAT THIS MODULE IS FOR IS STOPPING A BULK PAIRING, not performing one.
 *
 * The obvious reading of that instruction is "for each of the 3,139 conference
 * records, create its counterpart." That would have invented thousands of
 * championships that do not exist, and the research says so plainly:
 *
 *   · FOOTBALL crowns ONE. Every FBS conference stages a championship game and
 *     the title-game winner IS the champion — no separate regular-season
 *     champion is recognized or engraved. 98 football records here; pairing
 *     them would be 98 phantoms.
 *   · MEET SPORTS crown ONE. Cross country, track, swimming, golf, wrestling,
 *     gymnastics and rowing hold a single championship meet. There is no
 *     regular-season standings title to win. 1,285 records here.
 *   · AND THE PAIR IS NOT EVEN UNIVERSAL AMONG LEAGUE SPORTS. The NESCAC is the
 *     exact inverse of the default: football is its ONLY sport with a
 *     recognized regular-season champion, and every other sport crowns a
 *     tournament champion only. The Ivy League held NO basketball tournament
 *     until 2017 — the regular-season champion took the automatic bid.
 *
 * So the rule is per (sport × conference), it must be READ off the conference's
 * own site, and this module's job is to say **what we do not yet know** rather
 * than to guess. `expectation()` returns `unknown` far more often than it
 * returns an answer, and that is the correct behavior: a phantom championship
 * is worse than a missing one, because a rep works it and waits for a winner
 * who is never crowned.
 *
 * ⚠️ THE 3,127 EXISTING RECORDS DO NOT SAY WHICH TITLE THEY ARE. Of 3,139
 * conference records on the object, TWELVE name themselves regular-season or
 * tournament. So this is not only an acquisition problem — the records we hold
 * are under-specified, and creating a counterpart beside an ambiguous record
 * produces an ambiguous PAIR, which is worse than what we started with. That is
 * why `titleType()` exists and why the audit counts it as a real defect.
 * ========================================================================= */

const S = (v) => String(v ?? '').trim()
const L = (v) => S(v).toLowerCase()

/* ---- THE THREE TITLE TYPES -------------------------------------------- */
export const REGULAR = 'regular-season'
export const TOURNAMENT = 'tournament'
export const AMBIGUOUS = 'ambiguous'
export const SINGLE = 'single'          // the sport crowns only one; not a defect

/**
 * What does this record's NAME say it is?
 *
 * ⚠️ WORD-BOUNDARY MATCHING, NOT `includes`. The last time tokens were matched
 * loosely here, "NC" matched inside "NCHSAA" and a test agreed with the bug.
 * Two-letter and short tokens make that a certainty, not an edge case.
 */
export function titleType(record) {
  const name = S(record?.name ?? record?.raw?.championship_name ?? record?.championship_name)
  if (/\bregular[-\s]?season\b/i.test(name)) return REGULAR
  if (/\b(tournament|tourney)\b/i.test(name)) return TOURNAMENT
  /* A conference championship GAME is the tournament-equivalent in football —
   * it is a single-elimination postseason event, not a standings title. */
  if (/\bchampionship game\b/i.test(name)) return TOURNAMENT
  return AMBIGUOUS
}

/* ---- LEAGUE vs MEET ---------------------------------------------------- */

/**
 * A LEAGUE sport plays a round-robin season and then a bracket, so a pair is
 * POSSIBLE — not guaranteed (see NESCAC).
 */
export const LEAGUE_SPORTS = [
  'basketball', 'baseball', 'softball', 'soccer', 'volleyball', 'ice hockey',
  'lacrosse', 'field hockey', 'tennis', 'water polo', 'beach volleyball',
]

/**
 * A MEET sport holds ONE championship meet. There is no season-long standings
 * race to win, so there is nothing to pair. Recording a "regular-season cross
 * country championship" would be inventing a title outright.
 */
export const MEET_SPORTS = [
  'cross country', 'track & field', 'indoor track & field', 'outdoor track & field',
  'swimming & diving', 'golf', 'wrestling', 'gymnastics', 'rowing', 'skiing',
  'rifle', 'riflery', 'fencing', 'bowling',
]

/**
 * FOOTBALL IS ITS OWN CASE and is deliberately in neither list.
 *
 * It is a league sport by format, but every FBS conference now stages a
 * championship game (the Pac-12 reinstating one in 2026) and the title-game
 * winner is THE champion. No separate regular-season champion is recognized.
 * Putting it in LEAGUE_SPORTS would have generated 98 phantom regular-season
 * titles; putting it in MEET_SPORTS would misdescribe the format.
 *
 * ⚠️ Divisions are gone in the Big Ten, ACC, MAC and SEC (after 2023) and the
 * Big 12 (since 2022) — the top two by conference standings now meet in the
 * title game rather than two division winners. That changes who PLAYS, not how
 * many champions are crowned.
 */
export const FOOTBALL = 'football'

export function sportClass(sport) {
  const s = L(sport)
  if (s === FOOTBALL) return 'football'
  if (LEAGUE_SPORTS.includes(s)) return 'league'
  if (MEET_SPORTS.includes(s)) return 'meet'
  return 'unclassified'
}

/* ---- VERIFIED PER-CONFERENCE EXCEPTIONS -------------------------------- */

/**
 * Conferences whose structure is known to DEPART from the league default.
 *
 * ⚠️ ONLY VERIFIED ENTRIES BELONG HERE. An unverified guess in this table is
 * worse than an absent one, because `expectation()` trusts it over the default
 * and would stop the very research that would have caught the error.
 */
export const CONFERENCE_EXCEPTIONS = {
  /* The NESCAC is the exact inverse of the default: football is its only sport
   * with a recognized regular-season champion; every other sport crowns a
   * tournament champion only. Applying the league default here would create ~26
   * phantom regular-season titles in one conference. */
  'new england small college athletic conference': {
    note: 'Football is the ONLY sport with a recognized regular-season champion; every other sport crowns a tournament champion only.',
    leagueSports: TOURNAMENT,
    football: REGULAR,
  },
  /* The Ivy League ran NO basketball tournament until 2017 — the regular-season
   * champion took the automatic bid. Any Ivy basketball title recorded for a
   * season before 2017 is a REGULAR-SEASON title, and a tournament record for
   * those years would be a phantom.
   *
   * ⚠️ AND THE IVY HAS MOVED ON — the reputation is out of date. It is no longer
   * a regular-season-only league: baseball, softball, soccer, field hockey,
   * volleyball and lacrosse all added tournaments between 2010 and 2023,
   * wrestling in 2025 and squash in 2026. TENNIS and FENCING remain
   * regular-season only, and FOOTBALL has never had a title game — standings
   * decide it, and from 2025 the champion takes an automatic FCS playoff bid.
   * Recording an Ivy tennis or football tournament would be a phantom. */
  'ivy league': {
    note: 'No basketball tournament before 2017. Tennis and fencing remain regular-season only; football has never had a title game (standings decide it, with an automatic FCS bid from 2025).',
    tournamentFrom: { basketball: 2017 },
    sportOnly: { tennis: REGULAR, fencing: REGULAR, football: REGULAR },
  },
  /* ⚠️ THE AMERICAN IS THE WHOLE-CONFERENCE INVERSE. In nearly every sport the
   * postseason tournament winner is the SOLE official conference title — there
   * is no separate regular-season award to record. Applying the league default
   * across this conference would manufacture a regular-season championship in
   * every sport it sponsors. */
  'american athletic conference': {
    note: 'The American treats the postseason tournament winner as the sole official conference title in nearly every sport — there is no separate regular-season award.',
    leagueSports: TOURNAMENT,
  },
  /* Same body, the name it is usually written under. Duplicated deliberately:
   * `expectation()` looks up an exact normalized name, and a conference that
   * silently missed its own exception would fall back to the permissive
   * default — which is the failure this table exists to prevent. */
  'the american': {
    note: 'The American treats the postseason tournament winner as the sole official conference title in nearly every sport — there is no separate regular-season award.',
    leagueSports: TOURNAMENT,
  },
}

/**
 * Conferences that do NOT sponsor a sport at all — so a record for it is a
 * phantom, not a gap.
 *
 * ⚠️ THE POWER FOUR RECONCILIATION FOUND ELEVEN OF THESE ALREADY ON THE OBJECT.
 * They are the most expensive kind of bad record: a rep works one, waits for a
 * winner who is never crowned, and stops trusting the board. Every entry is
 * read off the conference's own sponsored-sport list.
 */
export const NOT_SPONSORED = {
  'big ten conference': ['volleyball:men', 'water polo:men', 'water polo:women'],
  'southeastern conference': ['soccer:men', 'lacrosse:men', 'lacrosse:women', 'gymnastics:men'],
  'atlantic coast conference': ['volleyball:men', 'water polo:men', 'water polo:women'],
  'big 12 conference': ['field hockey:women', 'lacrosse:men'],
  /* Big East football was discontinued in 2013 when the seven non-football
   * schools kept the name; the current Big East is basketball-centric. It DOES
   * sponsor men's and women's lacrosse, field hockey and wrestling.
   * ⚠️ WE HOLD A BIG EAST FOOTBALL CHAMPIONSHIP TODAY. It is a phantom. */
  'big east conference': ['football:men'],
  /* ⚠️ AND WE HOLD AN ATLANTIC 10 FOOTBALL CHAMPIONSHIP. A-10 football became
   * CAA Football in 2007 — the A-10 has not crowned a football champion since.
   * Both of these are on the object right now. */
  'atlantic 10 conference': ['football:men'],
  /* ⚠️ THE PATRIOT LEAGUE IS THE CLEAREST CASE OF "SPONSORS A SPORT" NOT
   * MEANING "CROWNS A CHAMPIONSHIP IN IT", and it is worth stating as the
   * general rule rather than a quirk. Army, Colgate and Holy Cross all field
   * ice hockey; none of it is a Patriot League title — it runs through Hockey
   * East and ECAC. Wrestling goes to the EIWA. Gymnastics, fencing, rifle,
   * sailing, squash, water polo and MEN'S rowing are all other conferences'
   * championships too. A school having a team is not evidence its conference
   * crowns a champion, and that inference is a phantom factory.
   *
   * VERIFIED LIVE from patriotleague.org's own rendered sport navigation:
   * exactly 22 sports, and none of the above is among them. */
  'patriot league': [
    'ice hockey:men', 'ice hockey:women', 'wrestling:men', 'gymnastics:men',
    'gymnastics:women', 'fencing:men', 'fencing:women', 'rifle:coed',
    'water polo:men', 'water polo:women', 'rowing:men',
  ],
  /* MVC football runs through the legally separate Missouri Valley Football
   * Conference — an MVFC title is not an MVC title. */
  'missouri valley conference': ['football:men'],
  'mountain west conference': ['soccer:men', 'water polo:women', 'beach volleyball:women'],
}

/* =========================================================================
 * ⚠️ HOW TO VERIFY A CONFERENCE — READ THIS BEFORE THE NEXT BATCH.
 *
 * EVERY Division I conference site is a JavaScript-rendered SPA (they are
 * nearly all SIDEARM Sports). A plain fetch returns an EMPTY TEMPLATE SHELL,
 * not a 404 — so a research pass that fetches them looks like it worked and
 * silently falls back to secondary sources for all of it. That happened on a
 * nine-conference batch here: sunbeltsports.org, getsomemaction.com,
 * caasports.com, bigskyconf.com, soconsports.com, swac.org, meacsports.com,
 * bigwest.org and patriotleague.org ALL returned shells, and every "fact" in
 * that pass traced to Wikipedia.
 *
 * It is the same trap as psal.org, where a sourced "23" was really 68.
 *
 * AND IT MATTERS: bigwest.org's RENDERED navigation lists Football and Men's
 * Water Polo, both of which the secondary source said the Big West does not
 * sponsor. One of the two is wrong and a fetch cannot tell you which — so
 * NOTHING from that batch was created.
 *
 * THE METHOD THAT WORKS, in order:
 *   1. Render the site in a real browser and read its own SPORT NAVIGATION —
 *      that list is the conference's own answer to "what do we sponsor".
 *   2. Better still, find the CHAMPIONSHIP SCHEDULE page. What a conference
 *      schedules a championship for is a stronger claim than what it lists a
 *      sport for, and it dates the event too.
 *   3. Two independent sources agreeing is the bar for creating a record.
 *      Patriot League cleared it (rendered nav + secondary agreed on all 22).
 * ========================================================================= */

/** Is this (conference × sport × gender) a championship that does not exist? */
export function isPhantom(conference, sport, gender) {
  const list = NOT_SPONSORED[L(conference)]
  if (!list) return false
  return list.includes(`${L(sport)}:${L(gender)}`)
}

/**
 * What should exist for this (conference × sport)?
 *
 * Returns one of:
 *   { pair: true }                    — both titles are expected
 *   { pair: false, only: <type> }     — exactly one, and which
 *   { pair: null,  reason }           — NOT ESTABLISHED; go read the source
 *
 * ⚠️ `null` IS THE COMMON ANSWER AND IS THE POINT. Outside football, the meet
 * sports and the two verified exceptions, whether a given conference crowns a
 * regular-season title is a fact about that conference that has to be read off
 * its own site. Returning a confident `pair: true` for all 1,788 league-sport
 * records is precisely the bulk pairing this module exists to prevent.
 */
export function expectation(conference, sport, season = null) {
  const cls = sportClass(sport)
  const ex = CONFERENCE_EXCEPTIONS[L(conference)]

  if (cls === 'meet') {
    return { pair: false, only: SINGLE, reason: 'A meet sport holds one championship meet — there is no standings title to win.' }
  }
  if (cls === 'football') {
    /* ⚠️ `sportOnly` MUST BE CONSULTED HERE TOO. It was not, and the Ivy League
     * — whose football title has NEVER been decided by a game — came back
     * "tournament" off the FBS default. That is a phantom championship for a
     * conference that plays FCS football and settles the title on standings,
     * with an automatic playoff bid from 2025. The per-sport rule was declared
     * correctly and simply never read on this branch. */
    if (ex?.sportOnly?.[FOOTBALL]) return { pair: false, only: ex.sportOnly[FOOTBALL], reason: ex.note }
    if (ex?.football) return { pair: false, only: ex.football, reason: ex.note }
    return { pair: false, only: TOURNAMENT, reason: 'Every FBS conference stages a championship game and its winner is the champion; no separate regular-season title is recognized.' }
  }
  if (cls === 'unclassified') {
    return { pair: null, reason: `"${S(sport)}" is not classified as a league or meet sport — decide before recording either title.` }
  }

  /* League sport. A per-SPORT rule beats the conference-wide one — the Ivy
   * League has tournaments in most sports but not tennis or fencing, so a
   * blanket answer either way would be wrong for half its book. */
  if (ex?.sportOnly?.[L(sport)]) return { pair: false, only: ex.sportOnly[L(sport)], reason: ex.note }
  if (ex?.leagueSports) return { pair: false, only: ex.leagueSports, reason: ex.note }
  if (ex?.tournamentFrom) {
    const from = ex.tournamentFrom[L(sport)]
    const year = Number(season)
    if (from && Number.isFinite(year)) {
      return year < from
        ? { pair: false, only: REGULAR, reason: ex.note }
        : { pair: true, reason: ex.note }
    }
  }
  return {
    pair: null,
    reason: 'Most league-sport conferences crown both, but it is not universal — the NESCAC crowns tournament-only and the Ivy League had no basketball tournament before 2017. Read this conference\'s own site before recording.',
  }
}

/**
 * The work list: conference records whose name does not say which title they
 * are, in a sport where two titles are possible.
 *
 * ⚠️ A MEET SPORT IS NOT A DEFECT. "Big Ten Men's Cross Country Championship"
 * is complete and unambiguous — there is only one. Flagging all 1,285 of them
 * would bury the ~1,700 records that genuinely need a decision under noise
 * nobody can act on, which is how an integrity page becomes wallpaper.
 */
export function needsTitleType(record) {
  const raw = record?.raw || record || {}
  const level = S(raw.sr_level ?? record?.level)
  const segment = S(raw.sr_segment ?? record?.segment)
  if (level !== 'Conference' && segment !== 'Conference') return false
  if (titleType(record) !== AMBIGUOUS) return false
  const cls = sportClass(raw.sr_sport ?? record?.sport)
  return cls === 'league' || cls === 'unclassified'
}
